import { useId } from 'react';
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TextArea from '@/components/atoms/TextArea';

import FormField from './FormField';

interface FormTextareaProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    label: string;
    placeholder?: string;
    rows?: number;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    errorMessage?: string;
    optional?: boolean;
}

export default function FormTextarea<TFieldValues extends FieldValues>(
    props: FormTextareaProps<TFieldValues>
) {
    const { control, name, label, placeholder, rows = 3, rules, errorMessage, optional } = props;
    const id = useId();
    const { t } = useTranslation('fishGroups');

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormField
                    htmlFor={id}
                    label={label}
                    optional={optional}
                    error={
                        fieldState.error
                            ? fieldState.error.message ||
                              errorMessage ||
                              t('actions.errors.required')
                            : null
                    }>
                    <TextArea
                        id={id}
                        placeholder={placeholder}
                        rows={rows}
                        invalid={Boolean(fieldState.error)}
                        name={field.name}
                        value={(field.value as string | undefined) ?? ''}
                        onBlur={field.onBlur}
                        onChange={(e) => field.onChange(e.target.value)}
                    />
                </FormField>
            )}
        />
    );
}
